import { default as React, useEffect, useMemo, useState } from "react";
import { PacmanLoader } from "react-spinners";
import { post, put } from "aws-amplify/api";
import { Alert, Button, Col, Form, ListGroup, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import EditableCell from "containers/common/EditableCell";
import { GenericTable } from "components/GenericTable";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";

import {
  createColumnHelper,
} from "@tanstack/react-table";

function MultilineTableCell({ value, rows, onCommit }) {
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  return (
    <Form.Control
      as="textarea"
      rows={rows}
      className="no-border form-control"
      value={localValue}
      onChange={(event) => {
        setLocalValue(event.target.value);
      }}
      onBlur={() => {
        onCommit(localValue);
      }}
    />
  );
}

function PropertyAddList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  user,
  cityscopeOptions,
  suburbOptions,
  propertyid,
}) {

  const dataSet = 'properties';
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingNewRecord, setSavingNewRecord] = useState(false);
  const createId = useMemo(() => createElementIdFactory("property-add-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);
  const getCityscopeOptionValue = (option) =>
    option?.value ?? option?.cityscope ?? option?.code ?? option?.label ?? "";
  const getCityscopeOptionLabel = (option) =>
    option?.label ?? option?.value ?? option?.cityscope ?? option?.code ?? "";
  const getSuburbOptionValue = (option) =>
    option?.value ??
    (option?.suburb && option?.state
      ? `${option.suburb} (${option.state})`
      : option?.suburb ?? option?.label ?? "");
  const getSuburbOptionLabel = (option) =>
    option?.label ??
    (option?.suburb && option?.state
      ? `${option.suburb} (${option.state})`
      : option?.suburb ?? option?.value ?? "");
  const normalizedCityscopeOptions = useMemo(
    () =>
      (cityscopeOptions || []).map((option) => {
        if (typeof option === "string") {
          return { value: option, label: option };
        }

        const value = getCityscopeOptionValue(option);
        const label = getCityscopeOptionLabel(option);
        return { ...option, value, label };
      }).filter((option) => option?.value),
    [cityscopeOptions]
  );
  const normalizedSuburbOptions = useMemo(
    () =>
      (suburbOptions || []).map((option) => {
        const fallbackValue =
          option?.suburb && option?.state
            ? `${option.suburb} (${option.state})`
            : option?.suburb ?? option?.value ?? option?.label ?? "";

        return {
          ...option,
          value: getSuburbOptionValue(option) || fallbackValue,
          label: getSuburbOptionLabel(option) || fallbackValue,
          suburb: option?.suburb ?? option?.value ?? option?.label ?? null,
          state: option?.state ?? null,
          postcode: option?.postcode ?? null,
        };
      }),
    [suburbOptions]
  );

  // --- TanStack Table Setup ---
  const tableData = data || [];
  const columnHelper = createColumnHelper();

  async function savePropertyRecord(record) {
    const recordToSave = {
      ...record,
      last_edited_by: user,
    };

    if (recordToSave.new_record === true) {
      const response = await post({
        apiName: "colin",
        path: "/properties/new",
        options: {
          body: recordToSave,
        },
      }).response;
      return response.body.json();
    }

    const response = await put({
      apiName: "colin",
      path: `/properties/${recordToSave.property_id}`,
      options: {
        body: recordToSave,
      },
    }).response;
    return response.body.json();
  }

  function buildPropertyKey(property) {
    let propertyKey = `${property.cityscope || ""}${(property.map_number || "").toString()}${
      property.map_number_suffix || ""
    }.${(property.property_number || "").toString()}${property.property_number_suffix || ""}`;

    if (property.unit_number !== "" && property.unit_number !== null) {
      propertyKey = `${propertyKey}.${(property.unit_number || "").toString()}${
        property.unit_number_suffix || ""
      }`;
    }

    return propertyKey;
  }

  function validateRelatedRecord(record) {
    if (propertyid && String(record.property_id) === String(propertyid)) {
      return [true, "You cannot edit the original record"];
    }
    return [true, null];
  }

  async function handleSaveAllRows(sourceData) {
    setShowAlert(false);
    const nextData = [...(Array.isArray(sourceData) ? sourceData : tableData)];

    for (let i = 0; i < nextData.length; i += 1) {
      nextData[i] = {
        ...nextData[i],
        property_key: buildPropertyKey(nextData[i]),
      };

      const validation = validateRelatedRecord(nextData[i]);
      if (validation[0] !== true) {
        setErrorMessage(validation[1]);
        setShowAlert(true);
        return false;
      }

      if (nextData[i]?.modified === true) {
        if (!savingNewRecord && nextData[i]?.new_record === true) {
          setSavingNewRecord(true);
        }

        try {
          const result = await savePropertyRecord({ ...nextData[i], force: true });
          if (result?.result === false) {
            setErrorMessage(result?.error_message || "Save failed");
            setShowAlert(true);
            setSavingNewRecord(false);
            return false;
          }

          nextData[i] = {
            ...nextData[i],
            property_id: result?.id ?? result?.property_id ?? nextData[i].property_id,
            new_record: false,
            modified: false,
          };
        } catch (error) {
          const backendMessage =
            error?.response?.data?.error_message ||
            error?.response?.error_message ||
            error?.error_message ||
            error?.message ||
            "Save failed";
          setErrorMessage(backendMessage);
          setShowAlert(true);
          setSavingNewRecord(false);
          return false;
        }
      }
    }

    setSavingNewRecord(false);
    updateTableState(nextData, dataSet);
    return true;
  }

  function updateLocalRowAndSave(rowIndex, patch) {
    const nextData = [...tableData];
    nextData[rowIndex] = {
      ...nextData[rowIndex],
      ...patch,
      modified: true,
    };

    updateTableState(nextData, dataSet);

    // Save from the patched snapshot so the selected option doesn't get reverted by stale state.
    setTimeout(() => {
      handleSaveAllRows(nextData);
    }, 0);
  }

  const columns = useMemo(() => [
    columnHelper.accessor("property_id", {
      id: 'property_id',
      header: 'Property ID',
      cell: e => <Link to={`../properties/${e.getValue()}`}>{e.getValue()}</Link>,
      size: 80,
    }),
    columnHelper.accessor("cityscope", {
      id: 'cityscope',
      header: 'Cityscope',
      cell: ({ row }) => {
        const cellValue = row.original?.cityscope;
        const selectedOption =
          normalizedCityscopeOptions.find(
            (option) => String(getCityscopeOptionValue(option)) === String(cellValue)
          ) ||
          (cellValue ? { value: cellValue, label: cellValue } : null);

        return (
          <Select
            value={selectedOption}
            onChange={(option) => {
              updateLocalRowAndSave(row.index, {
                cityscope: getCityscopeOptionValue(option) || null,
              });
            }}
            options={normalizedCityscopeOptions}
            onBlur={() => {}}
            isClearable={false}
            getOptionValue={(option) => String(getCityscopeOptionValue(option))}
            getOptionLabel={(option) => String(getCityscopeOptionLabel(option))}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        );
      },
      size: 90,
    }),
    columnHelper.accessor("map_number", {
      id: 'map_number',
      header: 'Map No',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("map_number_suffix", {
      id: 'map_number_suffix',
      header: 'Map No Suffix',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("property_number", {
      id: 'property_number',
      header: 'Property No',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("property_number_suffix", {
      id: 'property_number_suffix',
      header: 'Property No Suffix',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("unit_number", {
      id: 'unit_number',
      header: 'Unit No',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("unit_number_suffix", {
      id: 'unit_number_suffix',
      header: 'Unit No Suffix',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("unit_sort", {
      id: 'unit_sort',
      header: 'Unit Sort',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("description", {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const cellValue = row.original?.description ?? "";
        return (
          <MultilineTableCell
            value={cellValue}
            rows={(Math.abs(cellValue.length / 80) + 1).toString()}
            onCommit={(nextValue) => {
              updateLocalRowAndSave(row.index, { description: nextValue });
            }}
          />
        );
      },
      size: 300,
    }),
    columnHelper.accessor("unit_area", {
      id: 'unit_area',
      header: 'Area',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("unit_level_number", {
      id: 'unit_level_number',
      header: 'Unit Level No',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("bedrooms", {
      id: 'bedrooms',
      header: 'Bedrooms',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("parking_yes_no", {
      id: 'parking_yes_no',
      header: 'Parking?',
      cell: ({ row }) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "31px",
          }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            style={{
              width: "16px",
              height: "16px",
              margin: 0,
              cursor: "pointer",
              padding: 0,
            }}
            checked={!!row.original?.parking_yes_no}
            onChange={(event) => {
              updateLocalRowAndSave(row.index, {
                parking_yes_no: event.target.checked,
              });
            }}
          />
        </div>
      ),
      size: 70,
      style: { textAlign: "center" }
    }),
    columnHelper.accessor("parking_num", {
      id: 'parking_num',
      header: 'Parking No',
      cell: EditableCell,
      size: 70,
    }),
    columnHelper.accessor("suburb", {
      id: 'suburb',
      header: 'Suburb',
      cell: ({ row }) => {
        const cellValue = row.original?.suburb;
        const rowState = row.original?.state;
        const selectedValue =
          cellValue && rowState ? `${cellValue} (${rowState})` : cellValue;
        const selectedOption =
          normalizedSuburbOptions.find(
            (option) => option?.suburb === cellValue && option?.state === rowState
          ) ||
          normalizedSuburbOptions.find(
            (option) => String(getSuburbOptionValue(option)) === String(selectedValue)
          ) ||
          (cellValue
            ? { value: `${cellValue} (${rowState || ""})`, label: `${cellValue} (${rowState || ""})` }
            : null);

        return (
          <Select
            value={selectedOption}
            onChange={(option) => {
              const isNzCityscope = ["AK", "WN", "CC"].includes(row.original?.cityscope);
              updateLocalRowAndSave(row.index, {
                suburb: option?.suburb ?? null,
                state: isNzCityscope ? "NZ" : (option?.state ?? null),
                postcode: option?.postcode ?? null,
              });
            }}
            options={normalizedSuburbOptions}
            onBlur={() => {}}
            isClearable={false}
            getOptionValue={(option) => String(getSuburbOptionValue(option))}
            getOptionLabel={(option) => String(getSuburbOptionLabel(option))}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        );
      },
    }),
    columnHelper.accessor("zoning_text", {
      id: 'zoning_text',
      header: 'Zoning',
      cell: ({ row }) => {
        const cellValue = row.original?.zoning_text ?? "";
        return (
          <MultilineTableCell
            value={cellValue}
            rows={(Math.abs(cellValue.length / 80) + 1).toString()}
            onCommit={(nextValue) => {
              updateLocalRowAndSave(row.index, { zoning_text: nextValue });
            }}
          />
        );
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.property_id, {
      id: 'copy_action',
      header: '',
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={(e) => handleCopyRecord(row.original, e)}
        >
          Copy
        </Button>
      ),
      size: 65,
    }),
  ], [
    columnHelper,
    normalizedCityscopeOptions,
    normalizedSuburbOptions,
    handleCopyRecord,
    updateLocalRowAndSave,
  ]);

  // --- Data Fetching and Update Functions ---
  //     componentDidUpdate(prevProps) {
  //         if (data !== prevProps.data) {
  //             const data = data;
  //             setState({ data });
  //         }
  //     }

  async function handleCopyRecord(sourceRecord, event) {
    event.preventDefault();
    console.log("Copy record");
    let localData = [...data];
    const copiedProperty = sourceRecord;
    let newProperty = {
      cityscope: copiedProperty.cityscope,
      map_number: copiedProperty.map_number,
      map_number_suffix: copiedProperty.map_number_suffix,
      property_number: copiedProperty.property_number,
      property_number_suffix: copiedProperty.property_number_suffix,
      unit_number: copiedProperty.unit_number,
      unit_number_suffix: copiedProperty.unit_number_suffix,
      unit_sort: copiedProperty.unit_sort,
      parking_yes_no: copiedProperty.parking_yes_no,
      suburb: copiedProperty.suburb,
      state: copiedProperty.state,
      postcode: copiedProperty.postcode,
      bedrooms: copiedProperty.bedrooms,
      unit_area: copiedProperty.unit_area,
      unit_level_number: copiedProperty.unit_level_number,
      description: copiedProperty.description,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      property_id: null,
      origin_db: null,
      parking_num: null,
      typical_floor_area: null,
      storeys: null,
      building_area: null,
      year_built: null,
      year_refurb: null,
      site_area: null,
      entity_id_managing_agent: null,
      managing_agent: null,
      historical_listings: null,
      sales_text: null,
      frontages_text: null,
      zoning_text: copiedProperty.zoning_text,
      title_details: copiedProperty.title_details,
      valuations_text: null,
      sales_notes: null,
      description_notes: null,
      owner_notes: null,
      frontages_notes: null,
      occupants_notes: null,
      historical_notes: null,
      title_notes: null,
      valuations_notes: null,
      occupants_preamble: null,
      occupants_postamble: null,
      owner_preamble: null,
      owner_postamble: null,
      for_sale_line: null,
      nla_date: null,
      lease_term: null,
      description_postamble: null,
      sales_history_postamble: null,
      entity_contact_id_managing_agent: null,
      managing_agent_contact: null,
      internet: null,
      email: null,
      photo_required: false,
      photo_why: null,
      leasing_notes: null,
      title_last_searched: null,
      sub_divided_unit: false,
      auto_doors: null,
      created_date: null,
      last_edited_date: null
    };
    localData.unshift(newProperty);
    updateTableState(localData, dataSet);
  };

  // --- Render Functions ---
  function renderList(data) {
    if (!data) {
      return null
    }
    else {
      return [{}].concat(data).map(
        (d, i) =>
          i !== 0
            ? <Link key={i} to={`/properties/${d.property_key}`}>
              <ListGroup.Item key={i} as="a" header={d.property_key}>
                {d.property_key}
              </ListGroup.Item>
            </Link>
            : ""
      );
    }
  }

  function renderComponent() {
    return (
      <div className="componentRendering">
        <Modal show={showAlert} onHide={(e) => { setShowAlert(!showAlert) }} centered >
          <Modal.Header closeButton> <Modal.Title>Uh Oh!</Modal.Title></Modal.Header>
          <Modal.Body > <Alert variant="danger"  >{errorMessage}</Alert> </Modal.Body>
        </Modal>
        {(controlType === "table" || controlType === "table2") && <Row >
          <Col className="text-right" >
            {/* {controlType === "table" && <Button variant="secondary" size="sm" onClick={addRecord}>Add</Button>} */}
          </Col>
        </Row>}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : controlType === "table" ? (
              <GenericTable
                data={tableData}
                columns={columns}
                section={dataSet}
                elementIds={tableElementIds}
                pageSize={20}
                enablePagination={true}
                enableOriginalOrderSort={true}
                meta={{
                  updateData: (rowIndex, columnId, value) => {
                    updateTableState(dataSet, rowIndex, columnId, value);
                  },
                  handleSave: async ({ row, column, value }) => {
                    const rowIndex = row?.index;

                    if (typeof rowIndex === "number" && column?.id) {
                      const nextData = [...tableData];
                      nextData[rowIndex] = {
                        ...nextData[rowIndex],
                        [column.id]: value,
                        modified: true,
                      };

                      updateTableState(nextData, dataSet);
                      return handleSaveAllRows(nextData);
                    }

                    return handleSaveAllRows();
                  },
                  handleDelete: ({ row }) => {
                    deleteRecord(dataSet, row.original);
                  },
                }}
                rowClassName={(row) => (row.index % 2 === 0 ? "even" : "odd")}
              />
            ) : renderList(data)}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="PropertyAddSection">
      {isAuthenticated ? renderComponent() : console.log("User not authenticated.")}
    </div>
  );
}

export default PropertyAddList;